/**
 * Contact extraction to Person (piece 32; decisions 0117 and 1506). The sender
 * menu's Create a person asks the server what the message says about its sender
 * and opens the Add Person window pre-filled for review: the name from the
 * display name, the From address, and the company and title the message gives.
 * 🔨 Nothing is auto-committed; the person's Save is the only write.
 */
import type { EditField } from '../pages/recordPages';
import { mailMessagePath } from './client';

/** What Create a person reads before the window opens. 🔨 Relative to the API root. */
export const mailExtractionPath = (id: string): string => `${mailMessagePath(id)}/extraction`;

/** Where the pre-filled company came from: the signature block's company line, the sender's domain, or nowhere. */
export type ExtractionCompanySource = 'signature' | 'domain';

/** The server's answer on a message's sender, read when Create a person is pressed. */
export interface MessageExtraction {
  /** The Person the From address already resolves to; the window is not offered while it stands. */
  person: { id: string; name: string } | null;
  /** The title the signature block gives, or null. */
  title: string | null;
  /** The company the window opens on; null where neither the signature nor the domain names exactly one. */
  company: { id: string; displayName: string } | null;
  companyFrom: ExtractionCompanySource | null;
  /** The company line the signature carries where it matches no company here, so the window can say what it read. */
  companyNamed: string | null;
}

/**
 * 🔒 The fields the window adds to Person's own when it is opened from a message
 * (decision 1506): the company the employment is at, the work address it carries
 * and a title. 🔨 The company is required, because an employment without one is
 * not a state the system can be in (piece 32).
 */
export const PERSON_EXTRACTION_FIELDS: readonly EditField[] = [
  { key: 'companyId', label: 'Company', kind: 'company', required: true },
  { key: 'workEmail', label: 'Work email', kind: 'email', required: true },
  { key: 'title', label: 'Title', kind: 'text' },
];

/** What a signature block yields, each field best-effort and null where the block says nothing usable. */
export interface SignatureFields {
  title: string | null;
  company: string | null;
}

/** A line that ends the message's own text: the quoted history starts below it. */
const QUOTE_START = [/^on .{3,200} wrote:?$/i, /^-{2,}\s*original message\s*-{2,}$/i, /^-{2,}\s*forwarded message\s*-{2,}$/i, /^from:\s.+$/i, /^>/];
/** A line that is not a title or a company: an address, a link, a phone, a device footer or a legal notice. */
const NOT_A_FIELD = [/@/, /https?:\/\//i, /\bwww\./i, /^sent from /i, /confidential/i, /^(t|tel|p|ph|phone|m|mob|mobile|c|cell|f|fax|o|office|d|direct)\s*[:.]/i];
/** The words a company's name ends on, which tell a company line from a title line. */
const COMPANY_WORDS = /\b(inc|incorporated|llc|ltd|limited|corp|corporation|co|company|group|gmbh|plc|llp|lp|holdings|industries|systems|services|solutions|technologies|controls|partners|associates)\.?$/i;
/** The separators a one-line signature joins its parts with: "Sales Manager | Acme Inc". */
const SEPARATOR = /\s+[|•·–—-]\s+|\s*,\s+|\s+at\s+/i;
/** Lines past the name a signature block reaches before it is something else. */
const BLOCK_LINES = 6;

function digitsIn(line: string): number {
  return (line.match(/\d/g) ?? []).length;
}

function usable(line: string): boolean {
  if (line.length < 2 || line.length > 80) return false;
  if (NOT_A_FIELD.some((pattern) => pattern.test(line))) return false;
  // A line carrying a phone number or a street address is contact detail, not a title or a company.
  if (digitsIn(line) >= 4) return false;
  return line.split(/\s+/).length <= 8;
}

/**
 * 🔒 The best-effort signature-block fields (piece 32; decision 0117): the lines
 * under the sender's own name near the end of the message's own text, the quoted
 * history set aside. The first usable line is the title and the next the company,
 * swapped where only the second reads as a person's job; one line reads as a
 * company only where it ends on a company word. 🔨 A message whose text never
 * names the sender yields nothing, rather than a guess from the wrong lines.
 */
export function signatureBlockFields(text: string, displayName: string | null): SignatureFields {
  const none: SignatureFields = { title: null, company: null };
  const name = (displayName ?? '').replace(/["<>]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!name) return none;
  const first = name.split(' ')[0]!;
  const lines: string[] = [];
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.replace(/\s+/g, ' ').trim();
    if (QUOTE_START.some((pattern) => pattern.test(line))) break;
    if (line) lines.push(line);
  }
  const namedAt = (line: string): string | null => {
    const lower = line.toLowerCase().replace(/^[-–—\s]+/, '');
    for (const named of [name, first]) {
      if (lower === named) return '';
      if (lower.startsWith(`${named} `) || lower.startsWith(`${named},`) || lower.startsWith(`${named}|`)) return line.replace(/^[-–—\s]+/, '').slice(named.length);
    }
    return null;
  };
  let anchor = -1;
  let rest = '';
  for (let at = lines.length - 1; at >= 0; at -= 1) {
    const found = namedAt(lines[at]!);
    if (found !== null && lines.length - at <= BLOCK_LINES + 3) {
      anchor = at;
      rest = found;
      break;
    }
  }
  if (anchor < 0) return none;
  const parts: string[] = [];
  const add = (line: string) => {
    for (const piece of line.split(SEPARATOR)) {
      const part = piece.trim().replace(/^[|,•·–—-]\s*/, '');
      if (usable(part)) parts.push(part);
    }
  };
  add(rest);
  for (const line of lines.slice(anchor + 1, anchor + 1 + BLOCK_LINES)) add(line);
  const [one, two] = parts;
  if (!one) return none;
  if (!two) return COMPANY_WORDS.test(one) ? { title: null, company: one } : { title: one, company: null };
  if (COMPANY_WORDS.test(one) && !COMPANY_WORDS.test(two)) return { title: two, company: one };
  return { title: one, company: two };
}
